import { CAL_EVENT_GRACE_HOURS } from '../cal-event-grace-hours.js';
import { eventPhotoRequirementApplies } from '../event-photo-requirement-applies.js';
import { isPerProductFieldType } from '../is-per-product-field-type.js';
import { templateFieldGraceHours } from '../template-field-grace-hours.js';
import { templateFieldPhase } from '../template-field-phase.js';
const HOUR_MS = 3_600_000;
function stateFor(input, phase, graceHours) {
    if (phase === 'afterStart') {
        const graceEnd = input.dateEnd.getTime() + graceHours * HOUR_MS;
        return input.now.getTime() >= graceEnd ? 'overdue' : 'due';
    }
    return input.now.getTime() >= input.dateStart.getTime() ? 'missed' : 'due';
}
function tabFor(phase) {
    return phase === 'afterStart' ? 'during' : 'before';
}
export function computeOutstandingEventItems(input) {
    const items = [];
    const filledPerProductKeys = new Set([
        ...input.perProductValues
            .filter((entry) => entry.value !== null && entry.confirmed)
            .map((entry) => `${entry.fieldKey}:${entry.productId}`),
        ...input.categoryValues
            .filter((entry) => entry.value !== null && entry.confirmed)
            .map((entry) => `${entry.fieldKey}:${entry.categoryId}`),
    ]);
    const rowIds = [...input.productIds, ...input.categoryIds];
    const lateRowIds = new Set(input.lateRowIds);
    const everyRowIsLateAndSummaryOwed = rowIds.length > 0 &&
        rowIds.every((rowId) => lateRowIds.has(rowId)) &&
        input.requiresProductSummary &&
        !input.noProductsToReport;
    for (const field of input.fields) {
        if (field.visible === false || !(field.required ?? field.config.required))
            continue;
        const phase = templateFieldPhase(field.config);
        const perProduct = isPerProductFieldType(field.typeId);
        const requiredRowIds = phase === 'beforeStart' && !everyRowIsLateAndSummaryOwed
            ? rowIds.filter((rowId) => !lateRowIds.has(rowId))
            : rowIds;
        const noRowsYet = rowIds.length === 0;
        const needsRowsBeforeStart = phase === 'beforeStart' && input.requiresProductSummary && !input.noProductsToReport;
        const missing = perProduct
            ? noRowsYet
                ? needsRowsBeforeStart
                : requiredRowIds.some((rowId) => !filledPerProductKeys.has(`${field.key}:${rowId}`))
            : !field.filled;
        if (!missing)
            continue;
        items.push({
            kind: perProduct ? 'perProductField' : 'field',
            key: field.key,
            label: field.label,
            phase,
            state: stateFor(input, phase, templateFieldGraceHours(field.config)),
            tab: tabFor(phase),
        });
    }
    if (input.requiresProductSummary && rowIds.length === 0 && !input.noProductsToReport) {
        items.push({
            kind: 'products',
            key: 'products',
            label: 'Products',
            phase: 'afterStart',
            state: stateFor(input, 'afterStart', CAL_EVENT_GRACE_HOURS.default),
            tab: 'during',
        });
    }
    if (input.checkInRequired &&
        input.onSiteAssignees.some((assignee) => !assignee.isOwner && assignee.checkedInAt === null)) {
        items.push({
            kind: 'checkIn',
            key: 'checkIn',
            label: 'Check in',
            phase: 'afterStart',
            state: stateFor(input, 'afterStart', CAL_EVENT_GRACE_HOURS.default),
            tab: 'team',
        });
    }
    if (eventPhotoRequirementApplies(input.photoRequired, input.onSiteAssignees) &&
        input.photoCount === 0) {
        items.push({
            kind: 'photo',
            key: 'photo',
            label: 'Photo',
            phase: 'afterStart',
            state: stateFor(input, 'afterStart', CAL_EVENT_GRACE_HOURS.default),
            tab: 'photos',
        });
    }
    if (input.needsApproval && !input.fullyApproved) {
        items.push({
            kind: 'approval',
            key: 'approval',
            label: 'Approval',
            phase: 'beforeStart',
            state: stateFor(input, 'beforeStart', 0),
            tab: 'team',
        });
    }
    return items;
}
//# sourceMappingURL=compute-outstanding-event-items.js.map